import { MapDef } from "./tracks";

// Authoring a track by its shape rather than by control points: a list of
// straights and arcs, walked from the start line, with two straights left for
// the walk to size so it comes back to where it began. The output is control
// points a spline can be put through (TRACK-IMPROVEMENTS.md, TOOL-5). Maps
// already in `tracks.ts` keep their own points.

/**
 * One piece of the lap: a straight of some length in units, or an arc of a
 * radius and a turn in degrees (positive to the right, as everywhere else).
 * A straight of "close" is one of the two the walk sizes to close the loop.
 */
export type Segment = ["straight", number | "close"] | ["arc", number, number];

/** How far apart control points come on a straight: the spline is loose enough there. */
export const POINT_SPACING = 22;
/** On an arc, no more than this many radians between points, so a tight corner isn't cut short. */
const ARC_STEP = 0.5;

/**
 * Control points, in world units, for a lap of these segments, starting at the
 * origin heading along +x. There must be two "close" straights, in different
 * directions, and the arcs must turn a whole lap between them: 360° one way.
 */
export function pointsFromSegments(segments: Segment[], spacing = POINT_SPACING): [number, number][] {
  const turned = segments.reduce((sum, s) => sum + (s[0] === "arc" ? s[2] : 0), 0);
  if (Math.abs(Math.abs(turned) - 360) > 0.5) throw new Error(`the arcs turn ${turned}°, not 360°`);
  const closing = segments.flatMap((s, i) => (s[0] === "straight" && s[1] === "close" ? [i] : []));
  if (closing.length !== 2) throw new Error(`needs two "close" straights, has ${closing.length}`);

  // Walk it with the closing straights at length 0 to see where it ends up, and
  // which way each of them points; the two lengths that bring it home follow.
  const walk = (lengths: [number, number]) => {
    let [x, z, heading] = [0, 0, 0];
    const at: { x: number; z: number; heading: number }[] = [];
    let k = 0;
    for (const s of segments) {
      at.push({ x, z, heading });
      if (s[0] === "straight") {
        const len = s[1] === "close" ? lengths[k++] : s[1];
        x += Math.cos(heading) * len;
        z += Math.sin(heading) * len;
      } else {
        const [r, deg] = [s[1], (s[2] * Math.PI) / 180];
        // The centre is on the right for a positive turn: heading + 90°.
        const side = Math.sign(deg);
        const [cx, cz] = [x + Math.cos(heading + (Math.PI / 2) * side) * r, z + Math.sin(heading + (Math.PI / 2) * side) * r];
        heading += deg;
        x = cx - Math.cos(heading + (Math.PI / 2) * side) * r;
        z = cz - Math.sin(heading + (Math.PI / 2) * side) * r;
      }
    }
    at.push({ x, z, heading });
    return { x, z, at };
  };
  const end = walk([0, 0]);
  const [d1, d2] = closing.map((i) => walk([0, 0]).at[i].heading).map((h) => [Math.cos(h), Math.sin(h)]);
  const det = d1[0] * d2[1] - d1[1] * d2[0];
  if (Math.abs(det) < 0.1) throw new Error("the two closing straights point (nearly) the same way");
  // l1 d1 + l2 d2 = -end
  const l1 = (-end.x * d2[1] + end.z * d2[0]) / det;
  const l2 = (-d1[0] * end.z + d1[1] * end.x) / det;
  if (l1 < 0 || l2 < 0) throw new Error(`closing straights would be ${l1.toFixed(1)} and ${l2.toFixed(1)} long`);
  const { at } = walk([l1, l2]);

  const points: [number, number][] = [];
  let k = 0;
  segments.forEach((s, i) => {
    const from = at[i];
    if (s[0] === "straight") {
      const len = s[1] === "close" ? [l1, l2][k++] : s[1];
      const steps = Math.max(1, Math.round(len / spacing));
      for (let q = 0; q < steps; q++) points.push([from.x + Math.cos(from.heading) * ((len * q) / steps), from.z + Math.sin(from.heading) * ((len * q) / steps)]);
    } else {
      const [r, deg] = [s[1], (s[2] * Math.PI) / 180];
      const side = Math.sign(deg);
      const [cx, cz] = [from.x + Math.cos(from.heading + (Math.PI / 2) * side) * r, from.z + Math.sin(from.heading + (Math.PI / 2) * side) * r];
      const steps = Math.max(1, Math.ceil(Math.abs(deg) / ARC_STEP));
      for (let q = 0; q < steps; q++) {
        const heading = from.heading + (deg * q) / steps;
        points.push([cx - Math.cos(heading + (Math.PI / 2) * side) * r, cz - Math.sin(heading + (Math.PI / 2) * side) * r]);
      }
    }
  });
  return points;
}

/**
 * The debug override: a JSON map saved in localStorage as `kartTrack` is
 * merged over the chosen map at the start of a race, like `kartTuning`, so a
 * layout can be tried on the phone without a rebuild. Fields replace the
 * map's own (`theme` merges into it); `segments` stands in for `points`. A
 * broken one is ignored.
 */
export function mergeTrack(map: MapDef, saved: string | null): MapDef {
  if (!saved) return map;
  try {
    const { segments, theme, ...rest } = JSON.parse(saved) as Partial<MapDef> & { segments?: Segment[] };
    return {
      ...map,
      ...rest,
      theme: { ...map.theme, ...theme },
      ...(segments ? { points: pointsFromSegments(segments) } : {}),
    };
  } catch {
    // Not JSON, or a layout that doesn't close: race the map as built.
    return map;
  }
}

/** Debug mode's override from localStorage. */
export function trackOverride(map: MapDef): MapDef {
  try {
    return mergeTrack(map, localStorage.getItem("kartTrack"));
  } catch {
    return map;
  }
}

/**
 * Festivalpladsen's layout (NEW-1), from the ticket. The ticket's arcs turn 365°
 * and a lap can't close on that, so the run-out onto the line (H) turns 45°, not 50°.
 * Positive turns are to the right.
 */
export const FESTIVALPLADSEN: Segment[] = [
  // A: entrance straight
  ["straight", 120],
  // B: campsite chicane
  ["arc", 24, 40], ["straight", 8], ["arc", 20, -60], ["straight", 8], ["arc", 24, 50],
  // C: fence sweeper, flat out
  ["straight", 30], ["arc", 70, 60],
  // D: bar hook
  ["straight", 25], ["arc", 13, 90],
  // E: back straight, food-truck esses
  ["straight", "close"], ["arc", 45, -35], ["straight", 15], ["arc", 45, 35],
  // F: flick left
  ["straight", 25], ["arc", 35, -45],
  // G: main-stage carousel, tightening
  ["arc", 55, 90], ["arc", 36, 55], ["arc", 22, 35],
  // H: run-out onto the line
  ["straight", "close"], ["arc", 45, 45],
];
